import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const apiUrl: string = `${import.meta.env.VITE_API_URL}`;
const escapedApiUrl: string = apiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const environment: string = import.meta.env.MODE || import.meta.env.VITE_NODE_ENV || 'development';
const isProduction: boolean = environment === 'production';
const isStaging: boolean = environment === 'staging' || environment === 'recette';

// Persisted flag telling the next page load whether the user has already accepted
// analytics consent. Replay sample rates cannot be changed at runtime, so a fresh
// grant only takes effect on the next reload — this cached flag is what makes that
// bridge work.
const ANALYTICS_CONSENT_STORAGE_KEY = 'sh_analytics_consent_granted';

let sentryInitialized = false;

const readCachedAnalyticsConsent = (): boolean => {
  try {
    return window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const rememberAnalyticsConsent = (granted: boolean): void => {
  try {
    if (granted) {
      window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(ANALYTICS_CONSENT_STORAGE_KEY);
    }
  } catch {
    // localStorage may be blocked (private mode, quota). Swallow: replays stay off.
  }
};

export const initializeSentry = () => {
  if (sentryInitialized) {
    return;
  }

  if (!isProduction && !isStaging) {
    return;
  }

  // Replays stay off until analytics consent is granted. Since Sentry.init sample
  // rates are frozen at init time, a mid-session grant does NOT reactivate replays:
  // they come back on the next page load, when this cached flag is read here.
  const hasAnalyticsConsent = readCachedAnalyticsConsent();
  const replaysSessionSampleRate = hasAnalyticsConsent ? 0.1 : 0;
  const replaysOnErrorSampleRate = hasAnalyticsConsent ? 1.0 : 0;

  Sentry.init({
    dsn: 'https://0edf007b8dfd73097a2f6f92e19abb54@o4508839324549120.ingest.de.sentry.io/4508839586103376',
    // Never let the SDK auto-attach the client IP, cookies or headers.
    sendDefaultPii: false,
    // Safety net: even if some code path calls Sentry.setUser or the SDK infers a
    // user, strip identifying fields before the event leaves the browser.
    beforeSend: event => {
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.email;
        delete event.user.username;
      }
      return event;
    },
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [/^\//, new RegExp(`^${escapedApiUrl}`)],

    // Replay is capped when analytics consent is missing and only kicks back in on
    // the next reload after consent has been granted (see comment above).
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
  });
  sentryInitialized = true;

  console.log(
    hasAnalyticsConsent
      ? '[Consent] initialized Sentry with replays enabled (cached analytics consent).'
      : '[Consent] initialized Sentry with replays disabled (no analytics consent yet).'
  );
};
